import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { debounce } from "lodash";
import {
  alpha,
  Typography,
  Box,
  Container,
  Paper,
  IconButton,
  Button,
  Grid,
  Chip,
  CircularProgress
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import SearchBar from "components/SearchBar/SearchBar";
import FilterSidebar from "components/FilterSidebar/FilterSidebar";
import { fetchDataRequest } from "../../../redux/api/apiSlice";
import { searchQueryChanged, setSelectedFilters, resetFiltersAndSearch } from "../../../redux/searchAndFilter/searchAndFilterSlice";
import styles from "../../../components/Questionnaire/Questionneaire.module.css";
import Pagination from "components/Pagination/Pagination";
import { ArrowBack } from "@mui/icons-material";
import default_theme from "assets/themes/themes";
import { fetchLapsRequest } from "../../../redux/laps/lapSlice";

const DATASET_NAME = "LAP Catalog";
const searchDelay = 700;
const cardsPerPage = 12;

const LAPPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { datasets, pagination, loading } = useSelector((state) => state.api);
  const { searchQuery, selectedFilters: reduxSelectedFilters } = useSelector((state) => state.searchFilter);
  const authState = useSelector((state) => state.auth);
  const isAuthenticated = authState?.isAuthenticated ?? false;
  const legacyLaps = datasets?.[DATASET_NAME] || [];
  const cassandraLaps = useSelector((state) => state.laps?.list || []);
  const [currentPage, setCurrentPage] = useState(location.state?.currentPage || 1);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showFavorites, setShowFavorites] = useState(false);
  const [favorites, setFavorites] = useState(new Set());
  
  // Get filters for this dataset, memoized to prevent infinite loops
  const selectedFilters = useMemo(() => {
    return reduxSelectedFilters[DATASET_NAME] || {};
  }, [reduxSelectedFilters]);

  useEffect(() => {
    const saved = localStorage.getItem("lapFavorites");
    if (saved) setFavorites(new Set(JSON.parse(saved)));
  }, []);

  // Initialize Redux state from location state if present (only once on mount)
  useEffect(() => {
    if (location.state?.searchQuery) {
      dispatch(searchQueryChanged(location.state.searchQuery));
    }
    if (location.state?.selectedFilters) {
      // Restore filters to Redux state
      Object.keys(location.state.selectedFilters).forEach(category => {
        location.state.selectedFilters[category].forEach(value => {
          dispatch(setSelectedFilters({ category, value, dataset: DATASET_NAME }));
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce search query for API calls
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(() => searchQuery);
  
  useEffect(() => {
    const handler = debounce((value) => {
      setDebouncedSearchQuery(value);
    }, searchDelay);
    handler(searchQuery);
    return () => handler.cancel();
  }, [searchQuery]);

  // Create a stable string representation of filters for comparison
  const filtersKey = useMemo(() => {
    return JSON.stringify(selectedFilters);
  }, [selectedFilters]);

  useEffect(() => {
    if (!showFavorites) {
      dispatch(fetchDataRequest({
        datasetName: DATASET_NAME,
        searchQuery: debouncedSearchQuery,
        filters: selectedFilters,
        page: currentPage,
        pageSize: cardsPerPage
      }));
      
      // Fetch Cassandra LAPs
      dispatch(fetchLapsRequest());
    }
  }, [dispatch, debouncedSearchQuery, filtersKey, currentPage, showFavorites]);

  const paginationInfo = pagination?.[DATASET_NAME] || {};
  const totalPages = paginationInfo.totalPages || 1;
  const legacyTotalCount = paginationInfo.total || 0;
  const combinedTotalCount = legacyTotalCount + (cassandraLaps?.length || 0);


  const displayedEntries = useMemo(() => {
    // 1. Format Postgres (Legacy) LAPs
    const legacyEntries = legacyLaps.map((entry) => ({
        key: `legacy-${entry.entryId}`,
        entryId: entry.entryId,
        title: entry.combinedData?.solution_title || "Untitled LAP",
        description: entry.combinedData?.brief_description || "",
        source: "legacy",
        combinedData: entry.combinedData || {}, 
        raw: entry,
    }));

    // 2. Format Cassandra LAPs
    const cassandraEntries = cassandraLaps.map((lap) => {
        // Backend returns data inside combinedData, so we extract it here safely
        const data = lap.combinedData || lap; 
        const id = lap.entryId || data.id;

        return {
            key: `cassandra-${id}`,
            entryId: id,
            title: data.title || "Untitled LAP",
            description: data.description || "",
            source: "cassandra",
            combinedData: {
                solution_title: data.title,
                brief_description: data.description
            },
            raw: lap,
        };
    });

    // 3. Combine them
    const allEntries = [...legacyEntries, ...cassandraEntries];

    if (showFavorites) {
        return allEntries.filter((e) => favorites.has(e.entryId));
    }
    return allEntries;
  }, [legacyLaps, cassandraLaps, showFavorites, favorites]);

  const favoritesTotalPages = showFavorites 
    ? Math.max(1, Math.ceil(displayedEntries.length / cardsPerPage))
    : totalPages;

  const paginatedEntries = showFavorites
    ? displayedEntries.slice((currentPage - 1) * cardsPerPage, currentPage * cardsPerPage)
    : displayedEntries;

  const handleSearchChange = (e) => {
    dispatch(searchQueryChanged(e.target.value));
    setCurrentPage(1);
  };

  const handleFilterChange = (category, value) => {
    dispatch(setSelectedFilters({ category, value, dataset: DATASET_NAME }));
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    dispatch(resetFiltersAndSearch(DATASET_NAME));
    setCurrentPage(1);
  };

  const getActiveFiltersCount = () =>
    Object.values(selectedFilters).reduce((acc, vals) => acc + (vals ? vals.length : 0), 0);

  const handlePageChange = (_, newPage) => {
    setCurrentPage(newPage);
    window.scrollTo(0, 0);
  };

  const handleEntryClick = (entry) => {
    // Save current state before navigating.
    const stateObj = {
      fromCatalogList: true,
      searchQuery: searchQuery,
      selectedFilters: selectedFilters,
      currentPage: currentPage
    };

    if (entry.source === "cassandra") {
      // Send Cassandra LAPs to a new detail view (Update this path if you named it differently)
      navigate(`/solutions/rural-development/lap-details/${entry.entryId}`, {
        state: stateObj
      });
    } else {
      // Send legacy PostgreSQL LAPs to the old dynamo view
      navigate(`/solutions/rural-development/lap-dynamo/${entry.entryId}`, {
        state: stateObj
      });
    }
  };

  const toggleFavorite = (entryId, e) => {
    e?.stopPropagation();
    const next = new Set(favorites);
    next.has(entryId) ? next.delete(entryId) : next.add(entryId);
    setFavorites(next);
    localStorage.setItem("lapFavorites", JSON.stringify([...next]));
  };

  const handleBackClick = () => {
    if (showFavorites) {
      setShowFavorites(false);
      setCurrentPage(1);
    } else {
      navigate('/solutions/rural-development');
    }
  };

  const activeCount = getActiveFiltersCount();
  const displayCount = showFavorites ? displayedEntries.length : combinedTotalCount;

  return (
    <Box className={styles.pageWrapper}
      sx={{
        flex: 1,
        backgroundColor: default_theme.palette.primary.white,
        minHeight: '100%',
        padding: 0,
      }}
    >
      <Container maxWidth="xl">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            gap: 2,
            flexWrap: "wrap",
            width: "100%",
            minHeight: "60px",
          }}
        >
          <button className={styles.backButton} onClick={handleBackClick}>
            <ArrowBack />
            {showFavorites ? "Back to Catalogue" : "Back to Solutions"}
          </button>
          <Typography variant="h4" sx={{ 
            mb: { xs: 1, sm: 0 },
            flexGrow: { xs: 1, sm: 0 }, 
            minWidth: "fit-content",
          }}>
            {showFavorites ? "Favorite LAPs" : "Local Action Plans"}
          </Typography>

          <Box sx={{ 
            display: "flex", 
            gap: 1, 
            alignItems: "center",
            flexShrink: 0,
          }}>
            {/* <Button
              variant={showFavorites ? "contained" : "outlined"}
              startIcon={<FavoriteIcon />}
              onClick={() => { 
                setShowFavorites((v) => !v); 
                setCurrentPage(1); 
              }}
              size="small"
              sx={{
                minWidth: "120px",
                whiteSpace: "nowrap",
                color: showFavorites ? default_theme.palette.primary.main : default_theme.palette.other.main,
                backgroundColor: showFavorites ? default_theme.palette.other.main : "transparent",
                borderColor: default_theme.palette.other.main,
                "&:hover": {
                  backgroundColor: showFavorites ? default_theme.palette.other.tealDark : default_theme.palette.other.tealHover,
                  color: showFavorites ? default_theme.palette.primary.main : default_theme.palette.other.tealDark,
                },
              }}
            >
              Favorites ({favorites.size})
            </Button> */}

            {/* <Button
              variant={filtersOpen ? "contained" : "outlined"}
              startIcon={<FilterListIcon />}
              onClick={() => {
                setFiltersOpen((v) => !v);
              }}
              size="small"
              sx={{
                minWidth: "100px",
                whiteSpace: "nowrap",
                backgroundColor: filtersOpen ? default_theme.palette.other.main : "transparent",
                color: filtersOpen ? default_theme.palette.primary.main : default_theme.palette.other.main,
                borderColor: default_theme.palette.other.main,
                "&:hover": {
                  backgroundColor: filtersOpen ? default_theme.palette.other.main :  default_theme.palette.other.tealHover,
                  color: filtersOpen ? default_theme.palette.other.main : default_theme.palette.other.tealDark,
                  borderColor: default_theme.palette.other.tealDark,
                },
                "& .MuiButton-startIcon": {
                  color: filtersOpen ? default_theme.palette.primary.main : default_theme.palette.other.main,
                  marginRight: "8px",
                },
                "& .MuiSvgIcon-root": {
                  color: filtersOpen ? default_theme.palette.primary.main : default_theme.palette.other.main,
                },
                "&.MuiButton-outlined": {
                  color: default_theme.palette.other.main,
                  borderColor: default_theme.palette.other.main,
                },
                "&.MuiButton-contained": {
                  backgroundColor: default_theme.palette.other.main,
                  color: default_theme.palette.primary.main,
                },
              }}
            >
              Filters {activeCount > 0 ? `(${activeCount})` : ""}
            </Button> */}
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <FilterSidebar
            filtersOpen={filtersOpen}
            setFiltersOpen={setFiltersOpen}
            selectedFilters={selectedFilters}
            handleFilterChange={handleFilterChange}
            clearAllFilters={clearAllFilters}
            getActiveFiltersCount={getActiveFiltersCount}
          />
        </Box>

        {/* <SearchBar searchQuery={searchQuery} onSearchChange={handleSearchChange} /> */}

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {loading && !showFavorites ? (
              <Box display="inline-flex" alignItems="center">
                <CircularProgress size={16} thickness={4} color={default_theme.palette.primary.black}/> 
                <Box ml={1}>Loading...</Box> 
              </Box>
            ) : (
              <>
                {showFavorites ? "Favorite" : "Showing"} {displayCount} LAP
                {displayCount !== 1 ? "s" : ""}
                {activeCount > 0 && !showFavorites &&
                  ` with ${activeCount} filter${activeCount !== 1 ? "s" : ""} applied`}
              </>
            )}
          </Typography>
        </Box>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          {paginatedEntries.map((entry) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={entry.entryId}>
              <Paper
                sx={{
                  p: 3,
                  cursor: "pointer",
                  height: 280,
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  borderRadius: "12px",
                  border: "1px solid",
                  borderColor: "divider",
                  "&:hover": {
                    backgroundColor: alpha(default_theme.palette.primary.gray, '0.05'),
                    transform: "translateY(-4px)",
                    boxShadow: default_theme.palette.other.cardShadow,
                  },
                  transition: "all 0.3s ease-in-out",
                }}
                onClick={() => handleEntryClick(entry)}
              >
                {isAuthenticated && (
                  <IconButton
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      color: favorites.has(entry.entryId) ? "error.main" : "text.secondary",
                      "&:hover": { backgroundColor: default_theme.palette.other.hoverLight },
                    }}
                    onClick={(e) => toggleFavorite(entry.entryId, e)}
                  >
                    {favorites.has(entry.entryId) ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                  </IconButton>
                )}

                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    pr: 4,
                    fontSize: "1.125rem",
                    fontWeight: "bold",
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    lineHeight: 1.3,
                  }}
                >
                  {entry.combinedData.solution_title || "Untitled LAP"}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    flex: 1,
                    display: "-webkit-box",
                    WebkitLineClamp: 6,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    lineHeight: 1.5,
                  }}
                >
                  {entry.combinedData.brief_description || entry.combinedData.solution_title || "Click to view details..."}
                </Typography>

                <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
                  <Chip label="LAP" size="small" variant="outlined" sx={{ fontSize: "0.75rem" }} />
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {favoritesTotalPages > 1 && (
          <Pagination
            count={favoritesTotalPages}
            page={currentPage}
            onPageChange={handlePageChange}
          />
        )}
      </Container>
    </Box>
  );
};

export default LAPPage;

